(() => {
  const API_KEY = 'WITabbedCollection';
  const ROOT_SELECTOR = '[data-wi-tabbed-collection]';

  const createAPI = () => {
    const instances = new Map();
    const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const getSectionId = (root) =>
      root.dataset.wiTabbedCollection || root.dataset.id || '';

    const createCarouselState = (carousel) => {
      const track = carousel.querySelector('[data-wi-carousel-track]');
      if (!track) return null;

      return {
        carousel,
        track,
        slides: Array.from(
          track.querySelectorAll('.wi-tabbed-collection__slide')
        ),
        previous: carousel.querySelector('[data-wi-carousel-previous]'),
        next: carousel.querySelector('[data-wi-carousel-next]'),
        current: carousel.querySelector('[data-wi-carousel-current]'),
        progress: carousel.querySelector('[data-wi-carousel-progress]'),
        progressBar: carousel.querySelector(
          '[data-wi-carousel-progress-bar]'
        ),
        controls: carousel.querySelector(
          '.wi-tabbed-collection__carousel-controls'
        ),
        step: 0,
        maxScroll: 0,
        scrollFrame: 0
      };
    };

    const measureCarousel = (state) => {
      if (!state || !state.track || state.track.clientWidth === 0) return false;

      const firstSlide = state.slides[0];
      const gap = parseFloat(getComputedStyle(state.track).gap) || 0;

      state.step = firstSlide
        ? firstSlide.getBoundingClientRect().width + gap
        : 0;

      state.maxScroll = Math.max(
        0,
        state.track.scrollWidth - state.track.clientWidth
      );

      return true;
    };

    const updateCarousel = (state, shouldMeasure = false) => {
      if (!state || !state.track) return;

      if (
        shouldMeasure ||
        !state.step ||
        state.track.clientWidth === 0
      ) {
        if (!measureCarousel(state)) return;
      } else {
        state.maxScroll = Math.max(
          0,
          state.track.scrollWidth - state.track.clientWidth
        );
      }

      const { track, slides } = state;
      const hasOverflow = state.maxScroll > 2;
      const scrollOffset = Math.abs(track.scrollLeft);
      const activeIndex =
        state.step > 0 ? Math.round(scrollOffset / state.step) : 0;

      if (state.controls) {
        state.controls.hidden = !hasOverflow;
      }

      if (!slides.length) return;

      if (state.current) {
        state.current.textContent = Math.min(
          slides.length,
          activeIndex + 1
        );
      }

      if (state.previous) {
        state.previous.disabled =
          !hasOverflow || scrollOffset <= 2;
      }

      if (state.next) {
        state.next.disabled =
          !hasOverflow ||
          scrollOffset >= state.maxScroll - 2;
      }

      const progressValue = track.scrollWidth
        ? Math.min(
            100,
            ((scrollOffset + track.clientWidth) /
              track.scrollWidth) *
              100
          )
        : 100;

      if (state.progress) {
        state.progress.setAttribute(
          'aria-valuenow',
          String(Math.round(progressValue))
        );
      }

      if (state.progressBar) {
        state.progressBar.style.transform =
          `scaleX(${progressValue / 100})`;
      }
    };

    const moveCarousel = (state, direction) => {
      if (!state || !state.track || !state.slides.length) return;

      if (!state.step) {
        measureCarousel(state);
      }

      if (!state.step) return;

      state.track.scrollBy({
        left: direction * state.step,
        behavior: reduceMotionQuery.matches ? 'auto' : 'smooth'
      });
    };

    const init = (root) => {
      if (!root || !root.matches(ROOT_SELECTOR)) return null;

      const sectionId = getSectionId(root);
      if (!sectionId) return null;

      instances.get(sectionId)?.destroy();

      const controller = new AbortController();
      const { signal } = controller;

      const tabs = Array.from(
        root.querySelectorAll('[data-wi-tab-button]')
      );
      const panels = Array.from(
        root.querySelectorAll('[data-wi-tab-panel]')
      );
      const descriptions = Array.from(
        root.querySelectorAll('[data-wi-tab-description]')
      );

      const panelByBlockId = new Map();
      panels.forEach((panel) => {
        panelByBlockId.set(panel.dataset.wiTabPanel, panel);
      });

      const descriptionByBlockId = new Map();
      descriptions.forEach((description) => {
        descriptionByBlockId.set(
          description.dataset.wiTabDescription,
          description
        );
      });

      const carouselStates = Array.from(
        root.querySelectorAll('[data-wi-carousel]')
      )
        .map(createCarouselState)
        .filter(Boolean);

      const carouselsByPanel = new Map();

      panels.forEach((panel) => {
        const panelStates = carouselStates.filter((state) =>
          panel.contains(state.carousel)
        );
        carouselsByPanel.set(panel, panelStates);
      });

      let equalHeightFrame = 0;
      let equalHeightSecondFrame = 0;

      const syncPanelCardHeights = (panel) => {
        if (!panel || panel.hidden) return;

        const cardShells = Array.from(
          panel.querySelectorAll(
            '.wi-tabbed-collection__card-shell .wi-product-card__shell'
          )
        );

        if (!cardShells.length) return;

        cardShells.forEach((cardShell) => {
          cardShell.style.removeProperty(
            '--wi-tabbed-equal-card-height'
          );
        });

        // Force one natural-layout read after clearing the previous value.
        void panel.offsetHeight;

        const tallestHeight = Math.ceil(
          cardShells.reduce((maximum, cardShell) => {
            const renderedHeight =
              cardShell.getBoundingClientRect().height;
            return Math.max(
              maximum,
              renderedHeight,
              cardShell.scrollHeight
            );
          }, 0)
        );

        if (!tallestHeight) return;

        const equalHeightValue = `${tallestHeight}px`;

        cardShells.forEach((cardShell) => {
          if (
            cardShell.style.getPropertyValue(
              '--wi-tabbed-equal-card-height'
            ) !== equalHeightValue
          ) {
            cardShell.style.setProperty(
              '--wi-tabbed-equal-card-height',
              equalHeightValue
            );
          }
        });
      };

      const schedulePanelCardHeights = (panel) => {
        if (!panel || panel.hidden) return;

        if (equalHeightFrame) {
          cancelAnimationFrame(equalHeightFrame);
        }

        if (equalHeightSecondFrame) {
          cancelAnimationFrame(equalHeightSecondFrame);
        }

        equalHeightFrame = requestAnimationFrame(() => {
          equalHeightSecondFrame = requestAnimationFrame(() => {
            syncPanelCardHeights(panel);
            equalHeightSecondFrame = 0;
          });

          equalHeightFrame = 0;
        });
      };

      const refreshPanel = (panel, shouldMeasure = false) => {
        if (!panel || panel.hidden) return;

        const states = carouselsByPanel.get(panel) || [];
        states.forEach((state) =>
          updateCarousel(state, shouldMeasure)
        );

        schedulePanelCardHeights(panel);
      };

      carouselStates.forEach((state) => {
        state.previous?.addEventListener(
          'click',
          () => moveCarousel(state, -1),
          { signal }
        );

        state.next?.addEventListener(
          'click',
          () => moveCarousel(state, 1),
          { signal }
        );

        state.track.addEventListener(
          'scroll',
          () => {
            if (state.scrollFrame) return;

            state.scrollFrame = requestAnimationFrame(() => {
              updateCarousel(state);
              state.scrollFrame = 0;
            });
          },
          { passive: true, signal }
        );

        state.track.addEventListener(
          'keydown',
          (event) => {
            if (
              event.key !== 'ArrowLeft' &&
              event.key !== 'ArrowRight'
            ) {
              return;
            }

            event.preventDefault();
            moveCarousel(
              state,
              event.key === 'ArrowRight' ? 1 : -1
            );
          },
          { signal }
        );
      });

      const activateTab = (tab, moveFocus = false) => {
        if (!tab) return;

        const blockId = tab.dataset.blockId;
        const activePanel = panelByBlockId.get(blockId);

        tabs.forEach((item) => {
          const isActive = item === tab;
          item.setAttribute(
            'aria-selected',
            isActive ? 'true' : 'false'
          );
          item.tabIndex = isActive ? 0 : -1;
        });

        panels.forEach((panel) => {
          panel.hidden = panel !== activePanel;
        });

        descriptions.forEach((description) => {
          description.hidden =
            description !== descriptionByBlockId.get(blockId);
        });

        if (moveFocus) {
          tab.focus({ preventScroll: true });
        }

        tab.scrollIntoView({
          block: 'nearest',
          inline: 'nearest'
        });

        requestAnimationFrame(() => {
          refreshPanel(activePanel, true);
        });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener(
          'click',
          () => activateTab(tab),
          { signal }
        );

        tab.addEventListener(
          'keydown',
          (event) => {
            let nextIndex = -1;

            if (event.key === 'ArrowRight') {
              nextIndex = (index + 1) % tabs.length;
            } else if (event.key === 'ArrowLeft') {
              nextIndex =
                (index - 1 + tabs.length) % tabs.length;
            } else if (event.key === 'Home') {
              nextIndex = 0;
            } else if (event.key === 'End') {
              nextIndex = tabs.length - 1;
            }

            if (nextIndex < 0) return;

            event.preventDefault();
            activateTab(tabs[nextIndex], true);
          },
          { signal }
        );
      });

      const handleBlockSelect = (event) => {
        const selectedBlockId = event.detail?.blockId;
        if (!selectedBlockId) return;

        const tab = root.querySelector(
          `[data-wi-tab-button][data-block-id="${CSS.escape(
            selectedBlockId
          )}"]`
        );

        activateTab(tab);
      };

      root.addEventListener(
        'shopify:block:select',
        handleBlockSelect,
        { signal }
      );

      let resizeObserver = null;
      let resizeFrame = 0;
      let observedWidth = Math.round(
        root.getBoundingClientRect().width
      );

      const handleResize = () => {
        if (resizeFrame) return;

        resizeFrame = requestAnimationFrame(() => {
          panels
            .filter((panel) => !panel.hidden)
            .forEach((panel) => refreshPanel(panel, true));

          resizeFrame = 0;
        });
      };

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => {
          const nextWidth = Math.round(
            root.getBoundingClientRect().width
          );

          // Equalising card heights changes only the root height.
          // Ignore those callbacks to avoid a ResizeObserver loop.
          if (nextWidth === observedWidth) return;

          observedWidth = nextWidth;
          handleResize();
        });
        resizeObserver.observe(root);
      } else {
        window.addEventListener(
          'resize',
          handleResize,
          { passive: true, signal }
        );
      }

      root.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;

        image.addEventListener(
          'load',
          () => {
            panels
              .filter((panel) => !panel.hidden)
              .forEach((panel) => refreshPanel(panel, true));
          },
          { once: true, signal }
        );
      });

      root.addEventListener(
        'click',
        (event) => {
          if (
            !event.target.closest(
              '.wi-product-card__variant-pill'
            )
          ) {
            return;
          }

          panels
            .filter((panel) => !panel.hidden)
            .forEach((panel) => schedulePanelCardHeights(panel));
        },
        { signal }
      );

      root.addEventListener(
        'change',
        () => {
          panels
            .filter((panel) => !panel.hidden)
            .forEach((panel) => schedulePanelCardHeights(panel));
        },
        { signal }
      );

      document.fonts?.ready.then(() => {
        if (signal.aborted) return;

        panels
          .filter((panel) => !panel.hidden)
          .forEach((panel) => refreshPanel(panel, true));
      });

      const destroy = () => {
        controller.abort();
        resizeObserver?.disconnect();

        if (resizeFrame) {
          cancelAnimationFrame(resizeFrame);
        }

        if (equalHeightFrame) {
          cancelAnimationFrame(equalHeightFrame);
        }

        if (equalHeightSecondFrame) {
          cancelAnimationFrame(equalHeightSecondFrame);
        }

        carouselStates.forEach((state) => {
          if (state.scrollFrame) {
            cancelAnimationFrame(state.scrollFrame);
          }
        });

        if (instances.get(sectionId)?.root === root) {
          instances.delete(sectionId);
        }
      };

      const instance = {
        root,
        destroy,
        activateTab
      };

      instances.set(sectionId, instance);

      requestAnimationFrame(() => {
        panels
          .filter((panel) => !panel.hidden)
          .forEach((panel) => refreshPanel(panel, true));
      });

      return instance;
    };

    const initAll = (scope = document) => {
      if (scope.matches?.(ROOT_SELECTOR)) {
        init(scope);
      }

      scope
        .querySelectorAll?.(ROOT_SELECTOR)
        .forEach(init);
    };

    const destroyWithin = (scope) => {
      instances.forEach((instance) => {
        if (
          instance.root === scope ||
          scope.contains?.(instance.root)
        ) {
          instance.destroy();
        }
      });
    };

    document.addEventListener('shopify:section:load', (event) => {
      initAll(event.target);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyWithin(event.target);
    });

    return {
      init,
      initAll,
      destroyWithin
    };
  };

  window[API_KEY] = window[API_KEY] || createAPI();
  window[API_KEY].initAll(document);
})();
